import { get } from "lodash";
import { array, bool, number, object } from "prop-types";
import React, { useEffect, useState } from "react";
import { BrowserView, MobileView } from "react-device-detect";
import { useSelector } from "react-redux";

import { DfpComponent } from "../../ads/dfp-component";
import RecommendedStories from "../../story-templates/story-elements/RecommendedStories";
import TrendingStories from "../../story-templates/story-elements/TrendingStories";
import Listing500India from "../Listing500India";
import ProfileRanking from "../ProfileRanking";
import { LatestEditionMagazineBlock } from "../LatestEditionMagazineBlock";
import "./ad-component.m.css";

const AdSlot = ({ story, networkId, adIndexNo, pathIndex }) => {
  const [adComponent, setAdComponent] = useState(<></>);
  useEffect(() => {
    setAdComponent(
      <>
        <BrowserView>
          <DfpComponent
            adStyleName="ad-slot-size-728x90-300x250"
            id={`story-page-inpage-leaderboard${adIndexNo >= 0 ? "-" + adIndexNo : ""}`}
            path={
              story["story-template"] === "live-blog"
                ? `/${networkId}/RPSG_Fortune_Desktop/RPSG_FRTN_Desk_LIVBLG_Leaderboard_${pathIndex}`
                : story["story-template"] === "photo"
                ? `/${networkId}/RPSG_Fortune_Desktop/RPSG_FRTN_Desk_PHT_AS_Leaderboard_${pathIndex > 0 ? 2 : 1}`
                : `/${networkId}/RPSG_Fortune_Desktop/RPSG_FRTN_Desk_AS_Leaderboard_${pathIndex}`
            }
            adSlotConfigName="leaderboard_ad"
            showText={true}
          />
        </BrowserView>
        <MobileView>
          <DfpComponent
            adStyleName="ad-slot-size-300x250"
            id={`story-page-inpage-mRec-ad${adIndexNo >= 0 ? "-" + adIndexNo : ""}`}
            path={
              story["story-template"] === "live-blog"
                ? `/${networkId}/RPSG_Fortune_Mob/RPSG_FRTN_Mob_LIVBLG_mREC${
                    pathIndex - 1 > 0 ? "_" + (pathIndex - 1) : ""
                  }`
                : story["story-template"] === "photo"
                ? `/${networkId}/RPSG_Fortune_Mob/RPSG_FRTN_Mob_PHT_AS_mREC${pathIndex > 0 ? "_" + pathIndex : ""}`
                : `/${networkId}/RPSG_Fortune_Mob/RPSG_FRTN_Mob_AS_mREC${
                    pathIndex - 1 > 0 ? "_" + (pathIndex - 1) : ""
                  }`
            }
            adSlotConfigName="mRec_ad"
            showText={true}
          />
        </MobileView>
      </>
    );
  }, []);
  return adComponent;
};

const TaboolaInArticleAd = () => {
  const rawScript = `window._taboola = window._taboola || [];
  _taboola.push({
    mode: 'thumbnails-mid',
    container: 'taboola-mid-article-thumbnails',
    placement: 'Mid Article Thumbnails',
    target_type: 'mix'
  });`;

  return (
    <>
      <div id="taboola-mid-article-thumbnails" styleName="taboola"></div>
      <script type="text/javascript" dangerouslySetInnerHTML={{ __html: rawScript }}></script>
    </>
  );
};

const AdComponent = ({ adIndexNo, isDesktop, story, recommendedStories }) => {
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const qtConfig = get(qtState, ["config"], {});

  const widgetConfig = get(qtState, ["config", "ads-config", "widgets"], {});
  const networkId = get(qtState, ["config", "ads-config", "network_id"], {});
  const taboola = get(qtState, ["config", "publisher-attributes", "taboola"], true);
  const { magazineWidget = "", rankingWidget = "" } = widgetConfig;

  const getFilteredAddComp = (taboola) => {
    const original = {
      0: (
        <>
          {/* <GoAdFreeWidgetRed customStyleName="story-page-ad-free-container" /> */}
          <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={1} />
        </>
      ),
      1: <TaboolaInArticleAd />,
      2: (
        <>
          {magazineWidget.show ? (
            <LatestEditionMagazineBlock story={story} qtConfig={qtConfig} />
          ) : (
            <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={2} />
          )}
        </>
      ),
      3: (
        <RecommendedStories
          sectionId={story?.sections[0]?.id}
          storyId={story?.id}
          recommendedStories={recommendedStories}
          qtConfig={qtConfig}
        />
      ),
      4: <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={3} />,
      5: <></>,
      6: (
        <>
          {story["story-template"] === "live-blog" ? (
            <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={4} />
          ) : rankingWidget.show && rankingWidget.entityType && rankingWidget.entityType !== "" ? (
            rankingWidget.entityType === "FortyUnderForty" || rankingWidget.entityType === "MostPowerfulWomen" ? (
              <ProfileRanking
                collection={qtState.data.homeEntities[rankingWidget.entityType]}
                storyPage={true}
                config={qtState?.config}
                story={qtState?.data?.story}
              />
            ) : (
              <Listing500India
                collection={qtState.data.homeEntities[rankingWidget.entityType]}
                storyPage={true}
                story={qtState?.data?.story}
              />
            )
          ) : (
            <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={4} />
          )}
        </>
      ),
      7: <TrendingStories story={story} qtConfig={qtConfig} />,
      default: <></>,
    };

    if (taboola) {
      return original;
    }

    // Remove key "1" and reindex the rest (excluding default)
    const filtered = Object.entries(original).filter(([key]) => key !== "1" && key !== "default");

    const reindexed = filtered.reduce((acc, [, value], index) => {
      acc[index] = value;
      return acc;
    }, {});

    // Add back the default key
    reindexed.default = original.default;

    return reindexed;
  };

  const addComp = getFilteredAddComp(taboola);

  //Removing all ads and components/widgets from the body of sponsored articles
  if (story["story-template"] === "sponsored") {
    return null;
  }

  if (story["story-template"] === "photo") {
    return adIndexNo === 0 ? (
      <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={0} />
    ) : adIndexNo === 1 ? (
      <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={1} />
    ) : (
      <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={2} />
    );
  }

  return (
    <>
      {adIndexNo <= 6 ? addComp[`${adIndexNo}`] : null}
      {adIndexNo > 6 && <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={5} />}
    </>
  );
};

AdComponent.propTypes = {
  isDesktop: bool,
  adIndexNo: number,
  story: object,
  recommendedStories: array,
};

export default AdComponent;
